import { ButtonV2, Dialog } from '@cimplico/components';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { useWorkpaperContext } from '../providers/WorkpaperProvider';
import { WorkpaperSourceEnum } from '../types/workpaper';
import { toastNotification } from '../components/toast/Toast';
import { useRefreshXeroEntity } from '../queries/use-refresh-xero-entity';

type XeroDeepLinkProps = {
    reportId: number;
    label: string;
};

function XeroDeepLink({ reportId, label }: XeroDeepLinkProps): JSX.Element {
    const { provider, entity } = useWorkpaperContext();
    const xeroEntityShortCode = entity?.integrations.xero?.shortCode;

    const [refresh, setRefresh] = useState(false);

    return (
        <div>
            {refresh && entity?.id && (
                <RefreshXeroEntityConfirm
                    entityId={entity.id}
                    onXeroError={console.log}
                    onClose={(): void => setRefresh(false)}
                    onSync={(shortCode): void => {
                        setRefresh(false);
                        setTimeout(() => {
                            window.open(`https://reporting.xero.com/${shortCode}/v1/Run/${reportId}`, '_blank');
                        }, 1000);
                    }}
                />
            )}
            {provider === WorkpaperSourceEnum.XERO && xeroEntityShortCode && (
                <a
                    className="shrink-0"
                    href={`https://reporting.xero.com/${xeroEntityShortCode}/v1/Run/${reportId}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    <ButtonV2 type="hollow" label={label} size="sm" leftIcon={<ArrowTopRightOnSquareIcon />} />
                </a>
            )}
            {provider === WorkpaperSourceEnum.XERO && !xeroEntityShortCode && (
                <div className="shrink-0">
                    <ButtonV2
                        type="hollow"
                        label={label}
                        size="sm"
                        leftIcon={<ArrowTopRightOnSquareIcon />}
                        onClick={(): void => setRefresh(true)}
                    />
                </div>
            )}
        </div>
    );
}

type RefreshXeroEntityConfirmProps = {
    entityId: string;
    onClose: () => void;
    onSync: (shortCode?: string) => void;
    onXeroError: (error?: { name: string; message: string }) => void;
};

function RefreshXeroEntityConfirm({ entityId, onClose, onSync, onXeroError }: RefreshXeroEntityConfirmProps): JSX.Element {
    const refreshXeroEntityQuery = useRefreshXeroEntity(entityId);

    function refresh(): void {
        refreshXeroEntityQuery.mutate(
            { entityId, refreshShortCodeOnly: true },
            {
                onSuccess: ({ entity }) => {
                    toastNotification('success', 'Xero entity refreshed!');
                    onSync(entity.integrations.xero?.shortCode);
                },
                onError: (res) => {
                    const error = res.response?.data.error;
                    const message = res.response?.data.message;

                    if (error && message) {
                        onXeroError({ message, name: error });
                    }

                    toastNotification('error', 'Unable to refresh Xero entity!', message);
                    onClose();
                },
            }
        );
    }

    return (
        <Dialog
            isOpen
            title="Action Required"
            type="info"
            description="Cimplico Workpapers needs some more information from the Xero entity before opening this report. Click the refresh button below to automatically sync these details."
            primaryAction="Refresh"
            secondaryAction="Cancel"
            onClose={onClose}
            onPrimaryActionClick={refresh}
            onSecondaryActionClick={onClose}
            isLoading={refreshXeroEntityQuery.isLoading}
        />
    );
}

export default XeroDeepLink;
